<template>
  <div>
    <!-- No transition to prevent breaking chart animation -->
    <card-dialog
      persistent
      v-model="dialogVisible"
      :width="1000"
      :title="$t('label.rat_appearance')"
      :transition="false"
      content-class="rat-graph-dialog"
    >
      <template #activator="{ on, attrs }">
        <round-button icon="bar_chart" v-on="on" v-bind="attrs" :disabled="disabled" data-testid="rat_graph">
          {{ $t('button.appearance_graph') }}
        </round-button>
      </template>
      <div class="rat-graph pa-4">
        <v-progress-linear :indeterminate="!progress" :value="progress" v-if="!ready || !allEventsLoaded"/>
        <template v-else>
          <div class="d-flex align-center justify-space-between mb-3">
            <v-btn-toggle v-model="filterMode" mandatory dense color="primary">
              <v-btn
                value="exclude_misdetection"
                small
                data-testid="filter_exclude_misdetection"
              >{{ $t('filter.exclude_misdetection') }}</v-btn>
              <v-btn
                value="pickup_only"
                small
                data-testid="filter_pickup_only"
              >{{ $t('filter.pickup_only') }}</v-btn>
              <v-btn
                value="all"
                small
                data-testid="filter_all"
              >{{ $t('filter.all') }}</v-btn>
            </v-btn-toggle>
            <span class="detection-count" data-testid="detection_count">
              {{ $t('summary.detection_count', { count: eventsWithRat.length }) }}
            </span>
          </div>
          <bar-chart :data="chartData" :options="chartOptions" :styles="{ flex: '1', minHeight: '0' }"/>
          <div class="chart-caption text-right" data-testid="data_period">
            {{ dataPeriodStart
              ? $t('summary.data_period', { start: dataPeriodStart, end: dataPeriodEnd })
              : $t('summary.no_data_period')
            }}
          </div>
        </template>
      </div>
      <template #actions>
        <dialog-action @click="close">{{ $t('button.close') }}</dialog-action>
      </template>
    </card-dialog>
  </div>
</template>

<i18n lang="yaml">
ja:
  label:
    rat_appearance: 'ネズミの出現'
  filter:
    exclude_misdetection: '誤検出を除外'
    pickup_only: 'ピックアップのみ'
    all: 'すべて'
  graph:
    x_axis_time: '時間'
    y_axis_detection_count: '検出数'
  summary:
    detection_count: '検出数 {count} 件'
    data_period: 'データ期間: {start} 〜 {end}'
    no_data_period: 'データ期間: 該当なし'
  tooltip:
    count: '検出数: {count} 件'
  button:
    appearance_graph: '出現グラフ'
    close: '閉じる'
  msg:
    load_failed: データの読み込みに失敗しました。

en:
  label:
    rat_appearance: 'Rat Appearance'
  filter:
    exclude_misdetection: 'Exclude misdetections'
    pickup_only: 'Pickup only'
    all: 'All'
  graph:
    x_axis_time: 'Time'
    y_axis_detection_count: 'Detection count'
  summary:
    detection_count: 'Detection count: {count}'
    data_period: 'Data period: {start} - {end}'
    no_data_period: 'Data period: N/A'
  tooltip:
    count: 'Detection count: {count}'
  button:
    appearance_graph: 'Appearance Graph'
    close: 'Close'
  msg:
    load_failed: Failed to load data.
</i18n>

<script>
import { format, fromUnixTime, getHours } from 'date-fns';

import EventContainer from '@/mixins/eventContainer';
import Notifications from '@/mixins/notifications';

import BarChart from '@/components/atoms/BarChart';
import CardDialog from '@/components/atoms/CardDialog';
import DialogAction from '@/components/atoms/DialogAction';
import RoundButton from '@/components/atoms/RoundButton';

/** グラフ表示に必要な全イベントを少ないリクエスト回数で取得するための1回あたりの取得上限 */
const EVENTS_FETCH_LIMIT = 2000;

export default {
  name: 'rat-graph-button',
  mixins: [EventContainer, Notifications],
  components: {
    BarChart,
    CardDialog,
    DialogAction,
    RoundButton,
  },
  props: {
    // for EventContainer
    sensor: {
      type: Object,
      required: true,
    },
    requestParams: {
      type: Object,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    sensorId() {
      return this.sensor.id;
    },
    chartData() {
      return {
        labels: this.hourLabels,
        datasets: [
          {
            backgroundColor: '#0068b6',
            data: this.countsByHour,
          },
        ],
      };
    },
    chartOptions() {
      return {
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            callbacks: {
              label: (item) => this.$t('tooltip.count', { count: item.raw }),
            },
          },
        },
        scales: {
          xAxis: {
            title: {
              display: true,
              text: this.$t('graph.x_axis_time'),
            },
            ticks: {
              maxRotation: 0,
            },
          },
          yAxis: {
            title: {
              display: true,
              text: this.$t('graph.y_axis_detection_count'),
            },
            beginAtZero: true,
            max: this.yMax,
            ticks: {
              stepSize: 5,
            },
          },
        },
      };
    },
    countsByHour() {
      const result = this.hourLabels.map(() => 0);
      if (this.allEventsLoaded) {
        this.eventsWithRat.forEach((ev) => {
          const hour = getHours(fromUnixTime(ev.timestamp));
          result[hour] += 1;
        });
      }
      return result;
    },
    dataPeriodStart() {
      if (this.events.length === 0) return '';
      return format(fromUnixTime(this._.min(this.events.map(ev => ev.timestamp))), 'yyyy/MM/dd');
    },
    dataPeriodEnd() {
      if (this.events.length === 0) return '';
      return format(fromUnixTime(this._.max(this.events.map(ev => ev.timestamp))), 'yyyy/MM/dd');
    },
    eventsWithRat() {
      return this.events.filter((ev) => {
        if (!ev.detected) return false;
        if (this.filterMode === 'exclude_misdetection' && ev.misdetection) return false;
        if (this.filterMode === 'pickup_only' && !ev.pickup) return false;
        return true;
      });
    },
    hourLabels() {
      // Chart.js のスケール自動判定（数値→linear）を避けるため文字列で渡す
      return this._.range(24).map(String);
    },
    progress() {
      const { events } = this.eventsLoading;
      if (!this.oldestTimestamp || !events || events.length === 0) {
        return null;
      }
      const newestEvent = events[0];
      const totalTimeSpan = newestEvent.timestamp - this.oldestTimestamp;
      const lastEvent = events[events.length - 1];
      const loadedTimespan = newestEvent.timestamp - lastEvent.timestamp;

      return 100 * (loadedTimespan / totalTimeSpan);
    },
    yMax() {
      return Math.max(Math.ceil(this._.max(this.countsByHour) / 5) * 5, 5);
    },
  },
  data() {
    return {
      dialogVisible: false,
      filterMode: 'exclude_misdetection',
      ready: false,
      oldestTimestamp: null,
    };
  },
  methods: {
    close() {
      this.dialogVisible = false;
      this.ready = false;
      this.oldestTimestamp = null;
      if (!this.allEventsLoaded) {
        this.cancelEventLoading();
      }
    },
    loadGraph: async function () {
      try {
        await this.loadOldestEvent();
        await this.loadAllEvents({ limit: EVENTS_FETCH_LIMIT });
        this.ready = true;
      } catch (error) {
        this.notifyError('msg.load_failed');
      }
    },
    loadOldestEvent: async function () {
      const response = await this.getEvents({ limit: 1, ascending: '1' });
      const oldestEvent = response.events[0] || {};
      this.oldestTimestamp = oldestEvent.timestamp;
      this.clearEvents();
    },
  },
  watch: {
    dialogVisible(wasOpened) {
      if (wasOpened) {
        this.loadGraph();
      }
    },
    requestParams() {
      this.clearEvents();
    },
  },
};
</script>

<style lang="sass">
.rat-graph-dialog
  min-height: 600px
  max-height: 90vh

  > .v-card
    display: flex
    flex-direction: column
    min-height: inherit

    > .v-card__text
      flex: 1
      min-height: 0
      display: flex
      flex-direction: column

.rat-graph
  display: flex
  flex-direction: column
  height: 100%

  .detection-count
    font-size: 14px
    color: rgba(0, 0, 0, 0.7)

  .chart-caption
    font-size: 12px
    color: #9e9e9e
    margin-top: 4px
</style>
